import { type Day } from '@choco-admin/types';
import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useSupplierDeliverySettingQueryQuery } from '../../../generated';

interface UseSupplierDeliverySettingConfig {
  id: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useSupplierDeliverySetting = ({
  id,
  onSuccess,
  onError,
}: UseSupplierDeliverySettingConfig) => {
  const { addNotification } = useNotification();

  const { data, loading, error, refetch } =
    useSupplierDeliverySettingQueryQuery({
      variables: { id },
      skip: !id,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });

  const mapDeliveryDays = useCallback(() => {
    if (!data?.supplierDeliverySetting) return null;

    const { cutoffTimes, cutoffType } = data.supplierDeliverySetting;

    // Map days to boolean values - true if NoCutoffSetting or WithCutoffSetting
    const deliveryDays = {
      Monday:
        cutoffTimes?.mon?.__typename === 'NoCutoffSetting' ||
        cutoffTimes?.mon?.__typename === 'WithCutoffSetting',
      Tuesday:
        cutoffTimes?.tue?.__typename === 'NoCutoffSetting' ||
        cutoffTimes?.tue?.__typename === 'WithCutoffSetting',
      Wednesday:
        cutoffTimes?.wed?.__typename === 'NoCutoffSetting' ||
        cutoffTimes?.wed?.__typename === 'WithCutoffSetting',
      Thursday:
        cutoffTimes?.thu?.__typename === 'NoCutoffSetting' ||
        cutoffTimes?.thu?.__typename === 'WithCutoffSetting',
      Friday:
        cutoffTimes?.fri?.__typename === 'NoCutoffSetting' ||
        cutoffTimes?.fri?.__typename === 'WithCutoffSetting',
      Saturday:
        cutoffTimes?.sat?.__typename === 'NoCutoffSetting' ||
        cutoffTimes?.sat?.__typename === 'WithCutoffSetting',
      Sunday:
        cutoffTimes?.sun?.__typename === 'NoCutoffSetting' ||
        cutoffTimes?.sun?.__typename === 'WithCutoffSetting',
    } as Record<Day, boolean>;

    // Map cutoff times for each day - default to 23:59:59
    const cutoffTimesMap = {
      Monday:
        cutoffTimes?.mon?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.mon.cutoffTime
          ? cutoffTimes.mon.cutoffTime
          : '23:59:59',
      Tuesday:
        cutoffTimes?.tue?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.tue.cutoffTime
          ? cutoffTimes.tue.cutoffTime
          : '23:59:59',
      Wednesday:
        cutoffTimes?.wed?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.wed.cutoffTime
          ? cutoffTimes.wed.cutoffTime
          : '23:59:59',
      Thursday:
        cutoffTimes?.thu?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.thu.cutoffTime
          ? cutoffTimes.thu.cutoffTime
          : '23:59:59',
      Friday:
        cutoffTimes?.fri?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.fri.cutoffTime
          ? cutoffTimes.fri.cutoffTime
          : '23:59:59',
      Saturday:
        cutoffTimes?.sat?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.sat.cutoffTime
          ? cutoffTimes.sat.cutoffTime
          : '23:59:59',
      Sunday:
        cutoffTimes?.sun?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.sun.cutoffTime
          ? cutoffTimes.sun.cutoffTime
          : '23:59:59',
    } as Record<Day, string>;

    // Map cutoff days for each day - default to 1 day before
    const cutoffDaysMap = {
      Monday:
        cutoffTimes?.mon?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.mon.leadTime !== undefined
          ? cutoffTimes.mon.leadTime.toString()
          : '1',
      Tuesday:
        cutoffTimes?.tue?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.tue.leadTime !== undefined
          ? cutoffTimes.tue.leadTime.toString()
          : '1',
      Wednesday:
        cutoffTimes?.wed?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.wed.leadTime !== undefined
          ? cutoffTimes.wed.leadTime.toString()
          : '1',
      Thursday:
        cutoffTimes?.thu?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.thu.leadTime !== undefined
          ? cutoffTimes.thu.leadTime.toString()
          : '1',
      Friday:
        cutoffTimes?.fri?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.fri.leadTime !== undefined
          ? cutoffTimes.fri.leadTime.toString()
          : '1',
      Saturday:
        cutoffTimes?.sat?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.sat.leadTime !== undefined
          ? cutoffTimes.sat.leadTime.toString()
          : '1',
      Sunday:
        cutoffTimes?.sun?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.sun.leadTime !== undefined
          ? cutoffTimes.sun.leadTime.toString()
          : '1',
    } as Record<Day, string>;

    return {
      deliveryDays,
      cutoffTimes: cutoffTimesMap,
      cutoffDays: cutoffDaysMap,
      cutoffType:
        cutoffType?.toLowerCase() === 'nocutoff'
          ? 'no_cutoff'
          : (cutoffType?.toLowerCase() ?? 'no_cutoff'),
      hasCustomDeliverySettings: true,
    };
  }, [data]);

  // Handle errors with notifications
  if (error) {
    addNotification({
      title: 'Error',
      message: 'Failed to load delivery settings',
      type: 'error',
    });
  }

  return {
    deliverySetting: data?.supplierDeliverySetting,
    mappedSettings: mapDeliveryDays(),
    isLoading: loading,
    error,
    refetch,
  };
};
